import { createReducer, getUpdater } from '@advanza/advanza_generic'
import {
    CHANGE_LEAD_SETTING,
    PRESENCE_SUBSCRIPTION_ADD_DISCOUNT,
    PRESENCE_SUBSCRIPTION_ADD_ROLLOVER_DISCOUNT,
    PRESENCE_SUBSCRIPTION_DELETE_DISCOUNT,
    PRESENCE_SUBSCRIPTION_RECEIVE,
    PRESENCE_SUBSCRIPTION_REQUEST,
    PRESENCE_SUBSCRIPTION_UPDATE,
    PRESENCE_SUBSCRIPTION_UPDATE_REQUEST,
    PROVIDERS_CHANGE_FILTER,
    PROVIDERS_INVALIDATE,
    PROVIDERS_RECEIVE_LIST,
    PROVIDERS_RECEIVE_SINGLE,
    PROVIDERS_REQUEST,
} from 'actions/providers'
import { STATUS_INACTIVE } from 'interfaces/PresenceSubscription'
import {
    changeEntity,
    changeFilter,
    invalidateFilters,
    receiveList,
    selectItems,
} from 'reducers/sharedReducerHandlers'

const update = getUpdater()

const handlers = {
    [PROVIDERS_RECEIVE_LIST]: receiveList,
    [PROVIDERS_CHANGE_FILTER]: changeFilter,
    CHANGE_ENTITY_PROVIDERS: changeEntity,
    PROVIDERS_SELECT_ITEMS: selectItems,
    INVALIDATE_PROVIDERS: invalidateFilters,
    [PROVIDERS_REQUEST]: (state) =>
        update(state, {
            isFetching: { $set: true },
        }),
    [CHANGE_LEAD_SETTING]: (state, action) =>
        update(state, {
            entities: {
                leadSettings: { $merge: { [action.setting.setting_id]: action.setting } },
            },
        }),
    [PROVIDERS_RECEIVE_SINGLE]: (state, action) =>
        update(state, {
            isFetching: { $set: false },
            didInvalidate: { $set: false },
            entities: { $deepMerge: action.entities },
        }),
    [PROVIDERS_INVALIDATE]: (state, action) =>
        update(state, {
            didInvalidate: { $set: true },
        }),
    FRANCHISES_RECEIVE: (state, action) =>
        update(state, {
            franchises: { $set: action.response.franchises },
        }),
    FRANCHISES_ADD: (state, action) =>
        state.franchises
            ? update(state, {
                  franchises: { $push: action.franchises },
              })
            : state,
    PROFILE_BUILDER_WIZARD_CHANGE_SELECTION: (state, action) => {
        const { wizardSelection = {} } = state.entities.providers[action.providerId]
        const selection = wizardSelection[action.pageKey] || {}

        return update(state, {
            entities: {
                providers: {
                    [action.providerId]: {
                        wizardSelection: {
                            $set: {
                                ...wizardSelection,
                                [action.pageKey]: { ...selection, ...action.newSelection },
                            },
                        },
                    },
                },
            },
        })
    },
    CALENDLY_OPEN_MODAL: (state, action) =>
        update(state, {
            calendly: {
                isLoaded: { $set: true },
                providerId: { $set: action.providerId },
                calendlyEventId: { $set: action.calendlyEventId },
            },
        }),
    CALENDLY_CHECK_UPDATES: (state) =>
        update(state, {
            calendly: {
                checkForUpdates: { $set: true },
            },
        }),
    CALENDLY_UPDATES_COMPLETED: (state) =>
        update(state, {
            calendly: {
                checkForUpdates: { $set: false },
            },
        }),
    PLATFORMS_TRIGGER_REQUEST: (state) =>
        update(state, {
            platforms: {
                hasFetchedPlatforms: { $set: false },
            },
        }),
    PLATFORMS_REQUEST: (state) =>
        update(state, {
            platforms: {
                isFetchingPlatforms: { $set: true },
            },
        }),
    PLATFORMS_RECEIVE: (state, action) =>
        update(state, {
            platforms: {
                isFetchingPlatforms: { $set: false },
                hasFetchedPlatforms: { $set: true },
                platforms: { $set: action.platforms || state.platforms.platforms },
            },
        }),
    [PRESENCE_SUBSCRIPTION_REQUEST]: (state) =>
        update(state, {
            presenceSubscription: {
                isFetching: { $set: true },
            },
        }),
    [PRESENCE_SUBSCRIPTION_RECEIVE]: (state, action) =>
        update(state, {
            presenceSubscription: {
                isFetching: { $set: false },
                entities: {
                    [action.providerId]: {
                        $set: {
                            subscription: action.data.activeSubscription,
                            allowPresenceSubscription: action.data.allowPresenceSubscription,
                            serviceId: action.data.serviceId,
                            amount: action.data.amount,
                            overrideAmount:
                                action.data.activeSubscription?.subscription_override_amount,
                            error: action.data.error,
                            isUpdating: false,
                        },
                    },
                },
            },
        }),
    [PRESENCE_SUBSCRIPTION_UPDATE_REQUEST]: (state, action) =>
        update(state, {
            presenceSubscription: {
                entities: {
                    [action.providerId]: {
                        isUpdating: { $set: true },
                    },
                },
            },
        }),
    [PRESENCE_SUBSCRIPTION_UPDATE]: (state, action) => {
        const updatedSubscription = {
            ...state.presenceSubscription.entities[action.providerId].subscription,
            ...action.data.subscription,
        }

        if (action.data.subscription?.status === STATUS_INACTIVE) {
            updatedSubscription.presence_discounts = []
        }

        return update(state, {
            presenceSubscription: {
                entities: {
                    [action.providerId]: {
                        isUpdating: { $set: false },
                        subscription: {
                            $set: updatedSubscription,
                        },
                        overrideAmount: {
                            $set: action.data.subscription?.subscription_override_amount,
                        },
                    },
                },
            },
        })
    },
    [PRESENCE_SUBSCRIPTION_ADD_DISCOUNT]: (state, action) =>
        update(state, {
            presenceSubscription: {
                entities: {
                    [action.providerId]: {
                        subscription: {
                            presence_discounts: {
                                $set: [
                                    ...(state.presenceSubscription.entities[action.providerId]
                                        ?.subscription?.presence_discounts || []),
                                    action.data.discount,
                                ],
                            },
                        },
                    },
                },
            },
        }),
    [PRESENCE_SUBSCRIPTION_DELETE_DISCOUNT]: (state, action) =>
        update(state, {
            presenceSubscription: {
                entities: {
                    [action.providerId]: {
                        subscription: {
                            presence_discounts: {
                                $set: state.presenceSubscription.entities[
                                    action.providerId
                                ].subscription.presence_discounts.map((discount) =>
                                    discount.service_provider_presence_discount_id ===
                                    action.discountId
                                        ? {
                                              ...discount,
                                              status: 'inactive',
                                              end_date: action.data.discount.end_date,
                                          }
                                        : discount,
                                ),
                            },
                        },
                    },
                },
            },
        }),
    [PRESENCE_SUBSCRIPTION_ADD_ROLLOVER_DISCOUNT]: (state, action) =>
        update(state, {
            presenceSubscription: {
                entities: {
                    [action.providerId]: {
                        subscription: {
                            $set: action.data.subscription,
                        },
                    },
                },
            },
        }),
}

const initialState = {
    isFetching: false,
    didInvalidate: false,
    entities: {},
    result: {},
    counts: {},
    filters: {},
    presenceSubscription: {
        isFetching: false,
        entities: {},
    },
    calendly: {
        isLoaded: false,
        providerId: null,
        calendlyEventId: null,
        checkForUpdates: false,
    },
    platforms: {
        isFetchingPlatforms: false,
        hasFetchedPlatforms: false,
        platforms: {},
    },
}

export default createReducer(initialState, handlers)
